<script setup lang="ts" generic="T extends FilterOption">
import chevronDown from '@/assets/svgs/chevron_down.svg'
import chevronUp from '@/assets/svgs/chevron_up.svg'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { FilterOption } from '@/types/filter.types'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const isMobile = useIsMobile()
const props = defineProps<{
  options: T[]
  selectedOption: T
  width?: number
  customTrigger?: boolean
  menuAlign?: 'start' | 'end'
  alwaysShowPcVersion?: boolean
}>()

const emit = defineEmits<{
  select: [option: T]
}>()

const isOpen = ref(false)
const triggerRef = ref<HTMLElement | null>(null)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (option: T) => {
  emit('select', option)
  isOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (triggerRef.value && !triggerRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const selectedLabel = computed(() => {
  const selected = props.options.find((option) => option.idx === props.selectedOption.idx)
  return selected ? selected.name : props.options[0]?.name || ''
})

const closeBottomSheet = () => {
  isOpen.value = false
}
</script>

<template>
  <div class="filter-container" ref="triggerRef">
    <div class="filter-trigger" @click="toggleDropdown">
      <slot name="trigger" v-if="customTrigger" :isOpen="isOpen">
        <!-- 사용자 정의 트리거가 제공되는 경우 여기서 렌더링됩니다 -->
      </slot>
      <div v-else class="default-trigger">
        <span class="body-sm white">{{ selectedLabel }}</span>
        <img class="svg-24 svg-white" :src="isOpen ? chevronUp : chevronDown" alt="chevron" />
      </div>
    </div>

    <!-- PC 환경 드롭다운 -->
    <PerfectScrollbar
      v-if="isOpen && (!isMobile || props.alwaysShowPcVersion)"
      class="options-list"
      :class="{ 'menu-align-end': props.menuAlign === 'end' }"
      :style="{ width: props.width ? `${props.width}px` : '140px' }"
      :options="{
        maxScrollbarLength: 200,
        minScrollbarLength: 40,
        scrollYMarginOffset: 0,
        suppressScrollX: true,
      }"
    >
      <div
        v-for="option in options"
        :key="option.idx"
        class="option-item body-sm"
        @click="selectOption(option)"
      >
        {{ option.name }}
      </div>
    </PerfectScrollbar>

    <!-- 모바일 환경 바텀시트 -->
    <Teleport to="body" v-if="isOpen && isMobile && !props.alwaysShowPcVersion">
      <div v-if="isOpen" class="bottom-sheet-overlay" @click="closeBottomSheet"></div>
      <div v-if="isOpen" class="bottom-sheet">
        <div class="bottom-sheet-content">
          <div
            v-for="option in options"
            :key="option.idx"
            class="bottom-sheet-option-item body-md gray-600"
            :class="{ selected: option.idx === selectedOption.idx }"
            @click="selectOption(option)"
          >
            {{ option.name }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.filter-container {
  width: fit-content;
  position: relative;
}

.filter-trigger {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.default-trigger {
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 10px 6px 16px;
  background-color: var(--gray-900);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
}

.options-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  padding: 12px 16px;
  background-color: var(--gray-900);
  border-radius: 8px;
  border: 1px solid var(--white);
  z-index: 10;
  max-height: 334px;
  height: auto;
  overflow-y: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.options-list.menu-align-end {
  transform: translateX(-100%);
  left: 100%;
}

.option-item {
  cursor: pointer;
  color: var(--gray-600);
}

.option-item:hover {
  color: white;
}

/* 바텀시트 스타일 */
.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  bottom: 0;
  background: rgba(63, 65, 71, 0.4);
  z-index: 1000;
}

.bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 600px;
  border-radius: 8px 8px 0px 0px;
  background: var(--gray-900, #212121);
  z-index: 1001;
  padding: 32px 20px;
  animation: slide-up 0.3s ease-out;
}

.bottom-sheet-content {
  max-height: 50vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.bottom-sheet-option-item {
  padding: 8px 0;
  cursor: pointer;
  color: var(--gray-600);
}

.bottom-sheet-option-item.selected {
  color: white;
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (max-width: 1365px) {
}
</style>
